if (!customElements.get('collection-list-swiper')) {
  const SPEED = 300;

  // Matches the theme's sm/md/lg/xl breakpoints, widest first.
  const BREAKPOINTS = [
    [1280, 'slidesXl'],
    [1024, 'slidesLg'],
    [769, 'slidesMd'],
    [600, 'slidesSm']
  ];

  class CollectionListSwiper extends HTMLElement {
    constructor() {
      super();
      this.handleResize = this.handleResize.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleLockChange = this.handleLockChange.bind(this);
    }

    connectedCallback() {
      this.cancelSwiperLoad = theme.prepareSwiper(this, () => this.setup());
    }

    disconnectedCallback() {
      this.cancelSwiperLoad?.();
      clearTimeout(this.resizeTimer);
      window.removeEventListener('on:debounced-resize', this.handleResize);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.destroySwiper();
      this.initialized = false;
    }

    setup() {
      if (this.initialized) return;

      this.initialized = true;
      this.viewport = this.querySelector('.collection-list-swiper__viewport');
      this.wrapper = this.querySelector('.slider__grid');
      this.slides = [...this.querySelectorAll('.slider__item')];
      this.nextButton = this.querySelector('button[name="next"]');
      this.prevButton = this.querySelector('button[name="prev"]');

      window.addEventListener('on:debounced-resize', this.handleResize);
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }

      this.update();
    }

    /**
     * Auto hands slide sizing back to CSS, which is what a Custom card width needs:
     * those cards are sized by the merchant's value and, with "Widen cards to fit
     * titles" on, by their own title. A numeric slidesPerView would override both.
     */
    get autoWidth() {
      return this.dataset.slides === 'auto';
    }

    getSlidesPerView() {
      if (this.autoWidth) return 'auto';

      const breakpoint = BREAKPOINTS.find(([width]) => window.innerWidth >= width);
      return Number(this.dataset[breakpoint ? breakpoint[1] : 'slidesMobile']);
    }

    shouldEnable() {
      if (!this.viewport || !this.wrapper || this.slides.length < 2) return false;

      // Under auto widths the slide count says nothing about whether the row
      // overflows, so let Swiper measure and report back via lock/unlock.
      return this.autoWidth || this.slides.length > this.getSlidesPerView();
    }

    update() {
      if (!this.shouldEnable()) {
        this.destroySwiper();
        return;
      }

      if (!this.swiper) this.initSwiper();
    }

    initSwiper() {
      // Swiper spaces slides with margin, so the grid gap has to be handed over
      // rather than left to apply on top of it.
      const spaceBetween = parseFloat(getComputedStyle(this.wrapper).columnGap) || 0;
      const gutterValue = window.getComputedStyle(document.documentElement).getPropertyValue('--gutter').replace('px', '');

      this.viewport.classList.add('swiper');
      this.wrapper.classList.add('swiper-wrapper');
      this.wrapper.style.columnGap = '0px';
      this.slides.forEach((slide) => slide.classList.add('swiper-slide'));

      this.swiper = new window.Swiper(this.viewport, {
        breakpoints: this.autoWidth ? undefined : {
          600: { slidesPerView: Number(this.dataset.slidesSm) },
          769: { slidesPerView: Number(this.dataset.slidesMd) },
          1024: { slidesPerView: Number(this.dataset.slidesLg) },
          1280: { slidesPerView: Number(this.dataset.slidesXl) }
        },
        grabCursor: true,
        navigation: this.nextButton && this.prevButton ? {
          nextEl: this.nextButton,
          prevEl: this.prevButton
        } : false,
        on: {
          lock: this.handleLockChange,
          unlock: this.handleLockChange
        },
        slidesPerView: this.autoWidth ? 'auto' : Number(this.dataset.slidesMobile),
        spaceBetween,
        speed: SPEED,
        slidesOffsetBefore: gutterValue ? parseInt(gutterValue) : 0,
        slidesOffsetAfter: gutterValue ? parseInt(gutterValue) : 0,
        watchOverflow: true
      });

      this.handleLockChange();
    }

    destroySwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      if (this.wrapper) {
        this.wrapper.classList.remove('swiper-wrapper');
        this.wrapper.style.removeProperty('column-gap');
      }
      this.viewport?.classList.remove('swiper');
      this.slides?.forEach((slide) => slide.classList.remove('swiper-slide'));
      this.setAttribute('inactive', '');
    }

    /** `inactive` hides the arrows and the scrollbar gutter when nothing can scroll. */
    handleLockChange() {
      this.toggleAttribute('inactive', Boolean(this.swiper?.isLocked));
    }

    /** Brings the block a merchant just selected in the theme editor into view. */
    handleBlockSelect(evt) {
      if (!this.swiper || !this.contains(evt.target)) return;

      const index = this.slides.indexOf(evt.target.closest('.slider__item'));
      if (index > -1) this.swiper.slideTo(index);
    }

    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.destroySwiper();
        this.update();
      }, 100);
    }
  }

  customElements.define('collection-list-swiper', CollectionListSwiper);
}
